import styled, { css } from 'styled-components';
import { enterAnimation, pop } from '../styles/GlobalStyle';

/* ------------------------------------------------------------------ *
 * Layout
 * ------------------------------------------------------------------ */

export const Page = styled.main`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: clamp(16px, 4vw, 40px) clamp(14px, 4vw, 28px) 64px;
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 3vw, 26px);
`;

export const Stack = styled.div<{ $gap?: string; $align?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? '14px'};
  align-items: ${({ $align }) => $align ?? 'stretch'};
`;

export const Row = styled.div<{ $gap?: string; $wrap?: boolean; $justify?: string }>`
  display: flex;
  align-items: center;
  gap: ${({ $gap }) => $gap ?? '10px'};
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  justify-content: ${({ $justify }) => $justify ?? 'flex-start'};
`;

/* ------------------------------------------------------------------ *
 * Surfaces
 * ------------------------------------------------------------------ */

export const Card = styled.section`
  ${enterAnimation};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.shadows.puff};
  padding: clamp(18px, 3.5vw, 30px);
  /* A hairline highlight keeps white cards from vanishing into the pale backdrop. */
  border: 2px solid rgba(255, 255, 255, 0.9);
`;

export const SoftCard = styled(Card)`
  background: ${({ theme }) => theme.colors.surfaceSoft};
`;

/* ------------------------------------------------------------------ *
 * Type
 * ------------------------------------------------------------------ */

export const Title = styled.h1`
  font-size: clamp(2.1rem, 7vw, 3.4rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.02em;
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.colors.bubblegumDeep},
    ${({ theme }) => theme.colors.lavenderDeep} 45%,
    ${({ theme }) => theme.colors.sky2}
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-align: center;
`;

export const Heading = styled.h2`
  font-size: clamp(1.25rem, 3.6vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.01em;
`;

/* `display: block` so it still lays out correctly when rendered `as="span"`
   inside a <button>, where <p> would be invalid nesting. */
export const Subtle = styled.p<{ $center?: boolean }>`
  display: block;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: 0.98rem;
  line-height: 1.5;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
`;

export const Label = styled.label`
  display: block;
  font-weight: 700;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.inkSoft};
  margin-bottom: 8px;
`;

/** Small rounded tag, e.g. "host" or "you". */
export const Pill = styled.span<{ $tone?: 'lavender' | 'mint' | 'bubblegum' | 'lemon' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 11px;
  border-radius: ${({ theme }) => theme.radii.pill};
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  /* No text-transform here on purpose: pills carry real sentences on the reveal
     screen ("Loses a heart"), and the lowercase tags say so in the locale file. */
  ${({ theme, $tone = 'lavender' }) => {
    const map = {
      lavender: [theme.colors.lavender, '#4b3a70'],
      mint: [theme.colors.mint, '#1f6b4f'],
      bubblegum: [theme.colors.bubblegum, '#8a2e55'],
      lemon: [theme.colors.lemon, '#7a5b12'],
    } as const;
    const [bg, fg] = map[$tone];
    return css`
      background: ${bg};
      color: ${fg};
    `;
  }}
`;

/* ------------------------------------------------------------------ *
 * Controls
 * ------------------------------------------------------------------ */

type ButtonTone = 'primary' | 'secondary' | 'ghost' | 'danger';

const TONES = {
  primary: { bg: '#ff9ec4', lip: '#e0699a', fg: '#5a1f3b' },
  secondary: { bg: '#c9b6f7', lip: '#9d82dd', fg: '#3d2a63' },
  ghost: { bg: '#ffffff', lip: '#e4d6ec', fg: '#6d5c76' },
  danger: { bg: '#ff9aae', lip: '#e06b83', fg: '#6b1f30' },
} as const satisfies Record<ButtonTone, { bg: string; lip: string; fg: string }>;

/**
 * The chunky "lip" underneath is what sells the squishy feel: pressing the
 * button moves it down by exactly the lip height, so it looks like it compresses.
 */
export const Button = styled.button<{ $tone?: ButtonTone; $full?: boolean; $size?: 'sm' | 'md' | 'lg' }>`
  --lip: 6px;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: ${({ $full }) => ($full ? '100%' : 'auto')};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-weight: 800;
  letter-spacing: 0.01em;
  transition:
    transform 140ms ${({ theme }) => theme.transitions.bounce},
    box-shadow 140ms ease,
    filter 140ms ease;

  ${({ $size = 'md' }) =>
    ({
      sm: css`
        padding: 9px 18px;
        font-size: 0.9rem;
      `,
      md: css`
        padding: 13px 26px;
        font-size: 1.02rem;
      `,
      lg: css`
        padding: 18px 34px;
        font-size: 1.18rem;
      `,
    })[$size]}

  ${({ $tone = 'primary' }) => {
    const { bg, lip, fg } = TONES[$tone];
    return css`
      background: ${bg};
      color: ${fg};
      box-shadow: 0 var(--lip) 0 ${lip};
    `;
  }}

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.04);
  }

  &:active:not(:disabled) {
    transform: translateY(var(--lip));
    box-shadow: 0 0 0 transparent;
  }

  &:disabled {
    opacity: 0.5;
    box-shadow: 0 var(--lip) 0 rgba(0, 0, 0, 0.08);
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 14px 18px;
  border-radius: ${({ theme }) => theme.radii.pill};
  border: 2.5px solid ${({ theme }) => theme.colors.surfaceSunken};
  background: ${({ theme }) => theme.colors.cream};
  box-shadow: ${({ theme }) => theme.shadows.inset};
  font-weight: 600;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.inkFaint};
    font-weight: 500;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.bubblegum};
    box-shadow: 0 0 0 4px rgba(255, 179, 209, 0.32);
  }
`;

/** Room-code entry: wide letter spacing, always uppercase. */
export const CodeInput = styled(Input)`
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.42em;
  text-indent: 0.42em;
  font-weight: 800;
  font-size: 1.4rem;
`;

export const Banner = styled.div<{ $tone?: 'error' | 'info' }>`
  animation: ${pop} 300ms ${({ theme }) => theme.transitions.bounce} both;
  padding: 12px 18px;
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 700;
  font-size: 0.94rem;
  text-align: center;
  ${({ theme, $tone = 'error' }) =>
    $tone === 'error'
      ? css`
          background: #ffe1e8;
          color: #a03352;
        `
      : css`
          background: ${theme.colors.surfaceSoft};
          color: ${theme.colors.inkSoft};
        `}
`;
